import React, { FC, useEffect, useState } from 'react';
import { CropperTabletMobileProps } from '.';
import { Element, notchBottom, notchTop, scale } from '@components/frame';
import { isPhone } from '@helpers';
import { CropView } from 'react-native-image-crop-tools';
import { onCropMobileTablet } from '@v2/components/imageHandling/components/Cropper/helpers';
import { View, Image } from 'react-native';
import { SmallLoading } from '@components/loading';
import { calculateCropDimensions } from '@reducers/v2/imageHandlingV2/thunks/parseAndAddUrlsToImagesDocuments';

interface AndroidCropperProps extends CropperTabletMobileProps {
  base64Original: string | null;
}

export const AndroidCropper: FC<AndroidCropperProps> = (props) => {
  const { image, cropRef, angle, onSave, triggerShow, modeRef, triggerDiscardChangesPopup, base64Original, setShowChooseCropModeModalOpen, setPendingCropData, isNewImage = false, } = props;

  const [opacity, setOpacity] = useState<number>(0);
  const [actualImageDimensions, setActualImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [initPosition, setInitPosition] = useState<{ left: number; top: number; bottom: number; right: number } | undefined>(undefined);

  useEffect(() => {
    setTimeout(() => setOpacity(1), 500); // we need this because react-native-image-crop-tools library needs some time to handle rotation/crop calculation, so we can show correct image from the beginning without flickering
  }, []);

  useEffect(() => {
    if (base64Original) {
      Image.getSize(
        base64Original,
        (width, height) => {
          setActualImageDimensions({ width, height });

          // Calculate initial crop frame based on cropSettings if image is cropped
          if (image.isCropped && image.cropSettings?.length > 0) {
            const cropDimensions = calculateCropDimensions({
              width,
              height,
              cropSettings: image.cropSettings,
              pageNumber: image.pageNumber || 1,
              customAngle: angle,
            });
            // Android uses left, top, bottom, right format
            setInitPosition({
              left: cropDimensions.x,
              top: cropDimensions.y,
              bottom: cropDimensions.y + cropDimensions.height,
              right: cropDimensions.x + cropDimensions.width,
            });
          } else if (image.isCropped) {
            // Fallback for cropped images without cropSettings
            const { width: imageWidth = image.widthOriginal, height: imageHeight = image.heightOriginal, x = 0, y = 0 } = image;
            setInitPosition({
              left: x,
              top: y,
              bottom: y + imageHeight,
              right: x + imageWidth,
            });
          } else {
            // If not cropped at all, use full image dimensions
            // Android doesn't need initPosition for uncropped images, but we set it for consistency
            setInitPosition({
              left: 0,
              top: 0,
              bottom: height,
              right: width,
            });
          }
        },
        (error) => {
          console.error('Error getting image size:', error);
          // Fallback to original dimensions
          if (image.isCropped) {
            const { width: imageWidth = image.widthOriginal, height: imageHeight = image.heightOriginal, x = 0, y = 0 } = image;
            setInitPosition({
              left: x,
              top: y,
              bottom: y + imageHeight,
              right: x + imageWidth,
            });
          }
        }
      );
    }
  }, [base64Original, image, angle]);

  return (
    <Element
      key={'androidCropper'}
      {...{
        config: {
          position: {
            top: isPhone ? 51.5 + notchTop : 74,
            bottom: isPhone ? 52 + notchBottom : 74,
            right: isPhone ? 0 : 111,
            left: isPhone ? 0 : 111,
          },
        },
      }}
    >
      {(!base64Original || !opacity) && (
        <View style={{ marginTop: scale(40) }}>
          <SmallLoading />
        </View>
      )}
      {base64Original && (
        <CropView
          sourceUrl={base64Original}
          style={{ flex: 1, width: '100%', opacity }}
          ref={cropRef}
          onImageCrop={(res: any) =>
            onCropMobileTablet({
              angle,
              calculations: res,
              image: actualImageDimensions ? { ...image, widthOriginal: actualImageDimensions.width, heightOriginal: actualImageDimensions.height } : image,
              mode: modeRef.current,
              onSave,
              triggerDiscardChangesPopup,
              triggerShow,
              actualImageDimensions,
              setShowChooseCropModeModalOpen,
              setPendingCropData,
              isNewImage,
            })
          }
          initialImageCropFrame={initPosition as any}
          angle={angle || 0}
        />
      )}
    </Element>
  );
};
